/**
 * 权限控制指令
 * 提供 v-permission 和 v-role 指令用于模板中的权限控制
 */

import type { App, Directive, DirectiveBinding } from 'vue'
import { authManager, permissionManager } from '@/auth'
import { useAuth } from '@/composables/useAuth'
import { usePermissionStore } from '@/stores/permission'

/**
 * 权限指令类型
 */
type PermissionValue = string | string[] | {
  permission?: string | string[]
  role?: string | string[]
  mode?: 'any' | 'all'
  fallback?: 'hide' | 'disable' | 'readonly'
}

/**
 * API权限指令类型
 */
type ApiPermissionValue = string | {
  api: string
  fallback?: 'hide' | 'disable' | 'readonly'
}

/**
  * 标准化的权限名称格式
  * 所有权限名称应遵循 module:route:action 格式
  * 例如: system:user:view, tenant:list:view, home:dashboard:view
  */
export const PERMISSION_FORMAT_REGEX = /^[a-z][a-z0-9-]+:[a-z][a-z0-9-]+:[a-z][a-z0-9-]+$/

/**
 * 处理API权限检查
 */
function checkApiPermission(value: ApiPermissionValue): boolean {
  if (!value) return true

  const permissionStore = usePermissionStore()
  const apiPath = typeof value === 'string' ? value : value.api

  return permissionStore.hasApiPermission(apiPath)
}

/**
 * 处理权限检查
 */
function checkPermission(value: PermissionValue): boolean {
  if (!value) return true

  // 直接使用导入的权限管理器实例
  if (!permissionManager) {
    console.error('权限管理器实例不存在，权限检查失败')
    return false
  }

  try {
    // 字符串或数组形式的权限
    if (typeof value === 'string') {
      // 验证权限格式是否符合标准
      if (PERMISSION_FORMAT_REGEX.test(value)) {
        return permissionManager.hasPermission(value)
      } else {
        console.warn(`权限格式不符合标准: ${value}，应遵循 module:route:action 格式`)
        return false
      }
    }

    if (Array.isArray(value)) {
      // 对数组中的每个权限进行格式验证
      const validPermissions = value.filter(p => PERMISSION_FORMAT_REGEX.test(p))
      if (validPermissions.length !== value.length) {
        console.warn('部分权限格式不符合标准，应遵循 module:route:action 格式')
      }
      return validPermissions.some(p => permissionManager.hasPermission(p))
    }

    // 对象形式的复杂权限配置
    if (typeof value === 'object') {
      const { permission, role, mode = 'any' } = value

      let hasPermission = true
      let hasRole = true

      // 检查权限
      if (permission) {
        if (typeof permission === 'string') {
        // 验证单个权限格式
          if (PERMISSION_FORMAT_REGEX.test(permission)) {
            hasPermission = permissionManager.hasPermission(permission)
          } else {
            console.warn(`权限格式不符合标准: ${permission}，应遵循 module:route:action 格式`)
            hasPermission = false
          }
        } else if (Array.isArray(permission)) {
        // 验证数组中的权限格式
          const validPermissions = permission.filter(p => PERMISSION_FORMAT_REGEX.test(p))
          if (validPermissions.length !== permission.length) {
            console.warn('部分权限格式不符合标准，应遵循 module:route:action 格式')
          }
          hasPermission = mode === 'all'
            ? validPermissions.every(p => permissionManager.hasPermission(p))
            : validPermissions.some(p => permissionManager.hasPermission(p))
        }
      }

      // 检查角色
      if (role) {
        if (typeof role === 'string') {
          hasRole = permissionManager.hasRole(role)
        } else if (Array.isArray(role)) {
          hasRole = mode === 'all'
            ? role.every(r => permissionManager.hasRole(r))
            : role.some(r => permissionManager.hasRole(r))
        }
      }

      // 根据模式返回结果
      return mode === 'all' ? (hasPermission && hasRole) : (hasPermission || hasRole)
    }

    return false
  } catch (error) {
    console.error('权限检查过程中发生错误:', error)
    return false
  }
}

/**
 * 应用权限控制效果
 */
function applyPermissionEffect(
  el: HTMLElement,
  hasPermission: boolean,
  fallback: 'hide' | 'disable' | 'readonly' = 'hide'
) {
  if (hasPermission) {
    // 有权限时恢复元素状态
    el.style.display = ''
    el.removeAttribute('disabled')
    el.removeAttribute('readonly')
    el.classList.remove('permission-disabled', 'permission-readonly')
  } else {
    // 无权限时根据fallback策略处理
    switch (fallback) {
      case 'hide':
        el.style.display = 'none'
        break
      case 'disable':
        el.setAttribute('disabled', 'true')
        el.classList.add('permission-disabled')
        // 如果是按钮或输入框，添加禁用样式
        if (el.tagName === 'BUTTON' || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
          el.style.cursor = 'not-allowed'
          el.style.opacity = '0.6'
        }
        break
      case 'readonly':
        el.setAttribute('readonly', 'true')
        el.classList.add('permission-readonly')
        if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') {
          el.style.backgroundColor = '#f5f5f5'
          el.style.cursor = 'not-allowed'
        }
        break
    }
  }
}

/**
 * 权限指令 v-permission
 */
const permissionDirective: Directive<HTMLElement, PermissionValue> = {
  mounted(el, binding) {
    try {
      const { value } = binding
      const fallback = typeof value === 'object' && value.fallback ? value.fallback : 'hide'
      const hasPermission = checkPermission(value)

      // 保存原始状态
      el.dataset.originalDisplay = el.style.display || ''
      el.dataset.originalDisabled = el.getAttribute('disabled') || ''
      el.dataset.originalReadonly = el.getAttribute('readonly') || ''

      applyPermissionEffect(el, hasPermission, fallback)
    } catch (error) {
      console.error('权限指令mounted钩子执行失败:', error)
      // 发生错误时隐藏元素
      el.style.display = 'none'
    }
  },

  updated(el, binding) {
    const { value } = binding
    const fallback = typeof value === 'object' && value.fallback ? value.fallback : 'hide'
    const hasPermission = checkPermission(value)

    applyPermissionEffect(el, hasPermission, fallback)
  },

  unmounted(el) {
    // 清理添加的类和属性
    el.classList.remove('permission-disabled', 'permission-readonly')
    delete el.dataset.originalDisplay
    delete el.dataset.originalDisabled
    delete el.dataset.originalReadonly
  }
}

/**
 * 角色指令 v-role
 */
const roleDirective: Directive<HTMLElement, string | string[]> = {
  mounted(el, binding) {
    const { value } = binding
    let hasRole = false
    const { permissionManager } = authManager

    if (typeof value === 'string') {
      hasRole = permissionManager.hasRole(value)
    } else if (Array.isArray(value)) {
      hasRole = value.some(r => permissionManager.hasRole(r))
    }

    // 保存原始状态
    el.dataset.originalDisplay = el.style.display || ''

    if (!hasRole) {
      el.style.display = 'none'
    }
  },

  updated(el, binding) {
    const { value } = binding
    let hasRole = false
    const { permissionManager } = authManager

    if (typeof value === 'string') {
      hasRole = permissionManager.hasRole(value)
    } else if (Array.isArray(value)) {
      hasRole = value.some(r => permissionManager.hasRole(r))
    }

    if (hasRole) {
      el.style.display = el.dataset.originalDisplay || ''
    } else {
      el.style.display = 'none'
    }
  },

  unmounted(el) {
    delete el.dataset.originalDisplay
  }
}

/**
 * 超级管理员指令 v-super-admin
 */
const superAdminDirective: Directive<HTMLElement> = {
  mounted(el) {
    const { permissionManager } = authManager
    const isSuperAdmin = permissionManager.isSuperAdmin()

    el.dataset.originalDisplay = el.style.display || ''

    if (!isSuperAdmin) {
      el.style.display = 'none'
    }
  },

  updated(el) {
    const { permissionManager } = authManager
    const isSuperAdmin = permissionManager.isSuperAdmin()

    if (isSuperAdmin) {
      el.style.display = el.dataset.originalDisplay || ''
    } else {
      el.style.display = 'none'
    }
  },

  unmounted(el) {
    delete el.dataset.originalDisplay
  }
}

/**
 * 租户管理员指令 v-tenant-admin
 */
const tenantAdminDirective: Directive<HTMLElement> = {
  mounted(el) {
    const { permissionManager } = authManager
    const isTenantAdmin = permissionManager.isTenantAdmin()

    el.dataset.originalDisplay = el.style.display || ''

    if (!isTenantAdmin) {
      el.style.display = 'none'
    }
  },

  updated(el) {
    const { permissionManager } = authManager
    const isTenantAdmin = permissionManager.isTenantAdmin()

    if (isTenantAdmin) {
      el.style.display = el.dataset.originalDisplay || ''
    } else {
      el.style.display = 'none'
    }
  },

  unmounted(el) {
    delete el.dataset.originalDisplay
  }
}

/**
 * 租户功能指令 v-tenant-feature
 */
const tenantFeatureDirective: Directive<HTMLElement, string> = {
  mounted(el, binding) {
    const { value: feature } = binding
    const { isFeatureEnabled } = useAuth()

    el.dataset.originalDisplay = el.style.display || ''

    if (!isFeatureEnabled(feature)) {
      el.style.display = 'none'
    }
  },

  updated(el, binding) {
    const { value: feature } = binding
    const { isFeatureEnabled } = useAuth()

    if (isFeatureEnabled(feature)) {
      el.style.display = el.dataset.originalDisplay || ''
    } else {
      el.style.display = 'none'
    }
  },

  unmounted(el) {
    delete el.dataset.originalDisplay
  }
}

/**
 * API权限指令 v-api-auth
 */
const apiPermissionDirective: Directive<HTMLElement, ApiPermissionValue> = {
  mounted(el, binding) {
    const { value } = binding
    const hasPermission = checkApiPermission(value)
    const fallback = typeof value === 'object' ? value.fallback || 'hide' : 'hide'

    el.dataset.originalDisplay = el.style.display || ''
    applyPermissionEffect(el, hasPermission, fallback)
  },

  updated(el, binding) {
    const { value } = binding
    const hasPermission = checkApiPermission(value)
    const fallback = typeof value === 'object' ? value.fallback || 'hide' : 'hide'

    applyPermissionEffect(el, hasPermission, fallback)
  },

  unmounted(el) {
    delete el.dataset.originalDisplay
  }
}

/**
 * 权限工具函数
 */
export const permissionUtils = {
  /**
   * 检查元素是否有权限
   */
  checkElementPermission(el: HTMLElement): boolean {
    const permissionAttr = el.getAttribute('v-permission')
    const roleAttr = el.getAttribute('v-role')

    if (permissionAttr) {
      try {
        const permission = JSON.parse(permissionAttr)
        return checkPermission(permission)
      } catch {
        return checkPermission(permissionAttr)
      }
    }

    if (roleAttr) {
      try {
        const roles = JSON.parse(roleAttr)
        return Array.isArray(roles) ? auth.roleAny(roles) : auth.role(roles)
      } catch {
        return auth.role(roleAttr)
      }
    }

    return true
  },

  /**
   * 批量更新权限元素
   */
  updatePermissionElements(container: HTMLElement = document.body) {
    const permissionElements = container.querySelectorAll('[v-permission], [v-role]')

    permissionElements.forEach(el => {
      if (el instanceof HTMLElement) {
        const hasPermission = this.checkElementPermission(el)
        applyPermissionEffect(el, hasPermission)
      }
    })
  },

  /**
   * 添加权限变更监听
   */
  watchPermissionChange(callback: () => void) {
    const { onPermissionChange } = useAuth()
    return onPermissionChange(callback)
  }
}

/**
 * 注册所有权限指令
 */
export function setupPermissionDirectives(app: App) {
  app.directive('permission', permissionDirective)
  app.directive('auth', permissionDirective) // 别名
  app.directive('role', roleDirective)
  app.directive('api-auth', apiPermissionDirective)
  app.directive('super-admin', superAdminDirective)
  app.directive('tenant-admin', tenantAdminDirective)
  app.directive('tenant-feature', tenantFeatureDirective)

  // 添加全局权限工具
  app.config.globalProperties.$permission = permissionUtils

}

/**
 * 权限指令样式
 */
export const permissionDirectiveStyles = `
.permission-disabled {
  cursor: not-allowed !important;
  opacity: 0.6 !important;
  pointer-events: none !important;
}

.permission-readonly {
  cursor: not-allowed !important;
  background-color: #f5f5f5 !important;
  pointer-events: none !important;
}

.permission-hidden {
  display: none !important;
}

/* 权限提示样式 */
.permission-tooltip {
  position: relative;
}

.permission-tooltip::after {
  content: attr(data-permission-message);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s;
  z-index: 1000;
}

.permission-tooltip:hover::after {
  opacity: 1;
}
`

// 自动注入样式
if (typeof window !== 'undefined') {
  const styleId = 'permission-directive-styles'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = permissionDirectiveStyles
    document.head.appendChild(style)
  }
}

export {
  permissionDirective,
  roleDirective,
  apiPermissionDirective,
  superAdminDirective,
  tenantAdminDirective,
  tenantFeatureDirective
}
